// Fragment "openHours" — the practitioner's side of open-hours booking
// (docs/booking-module/open-hours-design.md): the weekly schedule's services and buffer
// (WeeklyScheduleEditor.jsx, DayWindowsRow.jsx), extra hours (OverridesPanel.jsx,
// ExtraHoursDialog.jsx), the calendar's open-hours background layer and trailing buffer
// (BookingCalendar.jsx, EventDetailDialog.jsx), the reschedule list (RescheduleDialog.jsx)
// and the Overview's opening-hours card (Overview.jsx).
//
// The server refusals these screens explain rather than surface as raw codes are mapped
// in admin/booking/calendarUtils.js (ERROR_CODE_KEYS) and rescheduleErrors.js.
export default {
  fr: {
    "sched.templateServicesLabel": "Service(s) par défaut",
    "sched.windowServicesHint": "Ne rien cocher pour utiliser le(s) service(s) par défaut de l'horaire.",
    "sched.serviceOption": "{name} — {duration} min — {price}",
    "sched.noServices": "Aucun service actif. Créez d'abord un service : c'est lui qui fixe la durée d'un rendez-vous.",
    "sched.buffer": "Pause après chaque rendez-vous (min)",
    "sched.bufferHelp": "Temps de repos après chaque rendez-vous, avant le suivant. Elle peut dépasser l'heure de fermeture. 0 = rendez-vous enchaînés.",
    "sched.bufferRange": "Entrez un nombre entier de minutes entre 0 et {max}.",
    "sched.windowNeedsService": "Ce créneau horaire ne propose aucun service. Cochez-en un ici, ou choisissez des services par défaut ci-dessus.",
    "sched.windowNeedsCatalogue": "Ce créneau horaire ne propose aucun service. Créez d'abord un service dans Services : c'est lui qui fixe la durée d'un rendez-vous.",
    "err.invalidWindowService": "Un des services choisis dans l'horaire hebdomadaire n'est plus disponible dans votre catalogue. Rechargez la page et choisissez à nouveau.",
    "err.windowWithoutService": "Un créneau horaire ne propose aucun service : choisissez-en un pour chaque créneau, ou des services par défaut.",
    "err.extraHoursOverlap": "Ces horaires chevauchent vos heures supplémentaires du {date}. Raccourcissez le créneau ou supprimez d'abord ces heures supplémentaires.",
    "sched.extraHoursOverlap": "Ce créneau chevauche vos heures supplémentaires du {date}.",
    "sched.timezoneHelp": "Le fuseau de votre cabinet : les heures ci-dessous y sont exprimées, et vos clients les voient converties dans le leur.",
    "err.timeUnavailable": "Cet horaire n'est pas disponible : il vient d'être pris, ou il chevauche un autre rendez-vous.",
    "err.invalidDate": "Date invalide",
    "ovr.extraTitle": "Heures supplémentaires",
    "ovr.extraSubtitle": "Des heures d'ouverture ponctuelles, en dehors de votre horaire hebdomadaire. Les horaires proposés y sont calculés comme dans votre horaire habituel.",
    "ovr.extraAdd": "Ajouter des heures",
    "ovr.extraEmpty": "Aucune heure supplémentaire à venir.",
    "ovr.extraServicesUnknown": "Service(s) retiré(s) du catalogue",
    "ovr.extraConfirmDelete": "Retirer ces heures supplémentaires ? Les rendez-vous déjà pris sont conservés.",
    "ovr.extraDeleted": "Heures supplémentaires retirées",
    "ovr.extraDialogTitle": "Ajouter des heures supplémentaires",
    "ovr.extraDialogDesc": "Ouvrez votre agenda un jour précis, pour les services de votre choix.",
    "ovr.extraDate": "Date",
    "ovr.extraStart": "De",
    "ovr.extraEnd": "À",
    "ovr.extraServices": "Service(s) proposé(s)",
    "ovr.extraReasonPlaceholder": "ex. : samedi exceptionnel",
    "ovr.extraCreate": "Ajouter ces heures",
    "ovr.extraCreated": "Heures supplémentaires ajoutées",
    "ovr.extraOverlap": "Ces heures chevauchent votre horaire hebdomadaire ou d'autres heures supplémentaires ce jour-là.",
    "ovr.extraDateRequired": "Choisissez une date",
    "ovr.extraTimesRequired": "Heure requise",
    "ovr.extraServicesRequired": "Cochez au moins un service",
    "ovr.extraReasonTooLong": "{max} caractères au maximum",
    "cal.openHoursDraft": "Votre horaire est un brouillon : les heures teintées ne sont pas encore proposées à vos clients. Publiez-le depuis Disponibilités.",
    "cal.openHoursLoadError": "Impossible de charger vos heures d'ouverture",
    "cal.bufferAfter": "+ {n} min de pause",
    "reschedule.err.sameTime": "Ce rendez-vous est déjà à cet horaire.",
    "reschedule.err.needsAttention": "Ce rendez-vous payé attend votre décision (conserver ou rembourser) : traitez-le depuis Rendez-vous avant de le déplacer.",
    "reschedule.err.timeUnavailable": "Cet horaire n'est plus libre. Choisissez-en un autre.",
    "reschedule.loadError": "Impossible de charger les horaires disponibles.",
    "reschedule.retry": "Réessayer",
    "overview.openHoursWeek": "heures d'ouverture sur 7 jours",
    "overview.openHoursDraft": "heures sur 7 jours (brouillon, pas encore réservables)",
  },
  en: {
    "sched.templateServicesLabel": "Default service(s)",
    "sched.windowServicesHint": "Leave all unchecked to use the schedule's default service(s).",
    "sched.serviceOption": "{name} — {duration} min — {price}",
    "sched.noServices": "No active service yet. Create a service first: it is what sets how long an appointment lasts.",
    "sched.buffer": "Buffer after each appointment (min)",
    "sched.bufferHelp": "Rest time after each appointment, before the next one. It may run past closing time. 0 = back-to-back.",
    "sched.bufferRange": "Enter a whole number of minutes between 0 and {max}.",
    "sched.windowNeedsService": "This window offers no service. Tick one here, or choose default services above.",
    "sched.windowNeedsCatalogue": "This window offers no service. Create a service in Services first: it is what sets how long an appointment lasts.",
    "err.invalidWindowService": "One of the weekly schedule's chosen services is no longer available in your catalogue. Reload the page and choose again.",
    "err.windowWithoutService": "A window offers no service: choose one for every window, or set default services.",
    "err.extraHoursOverlap": "These hours overlap your extra hours on {date}. Shorten the window or remove those extra hours first.",
    "sched.extraHoursOverlap": "This window overlaps your extra hours on {date}.",
    "sched.timezoneHelp": "Your practice's time zone: the hours below are in it, and clients see them converted to their own.",
    "err.timeUnavailable": "That time is not available: it was just taken, or it overlaps another appointment.",
    "err.invalidDate": "Invalid date",
    "ovr.extraTitle": "Extra hours",
    "ovr.extraSubtitle": "One-off opening hours outside your weekly schedule. The times offered inside them are computed exactly as in your usual hours.",
    "ovr.extraAdd": "Add extra hours",
    "ovr.extraEmpty": "No upcoming extra hours.",
    "ovr.extraServicesUnknown": "Service(s) removed from your catalogue",
    "ovr.extraConfirmDelete": "Remove these extra hours? Appointments already booked inside them are kept.",
    "ovr.extraDeleted": "Extra hours removed",
    "ovr.extraDialogTitle": "Add extra hours",
    "ovr.extraDialogDesc": "Open your calendar on one specific date, for the services you choose.",
    "ovr.extraDate": "Date",
    "ovr.extraStart": "From",
    "ovr.extraEnd": "To",
    "ovr.extraServices": "Service(s) offered",
    "ovr.extraReasonPlaceholder": "e.g. Special Saturday",
    "ovr.extraCreate": "Add these hours",
    "ovr.extraCreated": "Extra hours added",
    "ovr.extraOverlap": "These hours overlap your weekly schedule or other extra hours on that date.",
    "ovr.extraDateRequired": "Choose a date",
    "ovr.extraTimesRequired": "Time required",
    "ovr.extraServicesRequired": "Tick at least one service",
    "ovr.extraReasonTooLong": "{max} characters at most",
    "cal.openHoursDraft": "Your schedule is a draft: the tinted hours are not offered to your clients yet. Publish it from Availability.",
    "cal.openHoursLoadError": "Could not load your opening hours",
    "cal.bufferAfter": "+ {n} min buffer",
    "reschedule.err.sameTime": "This appointment is already at that time.",
    "reschedule.err.needsAttention": "This paid appointment is awaiting your decision (keep or refund): resolve it from Bookings before moving it.",
    "reschedule.err.timeUnavailable": "That time is no longer free. Choose another.",
    "reschedule.loadError": "Could not load the available times.",
    "reschedule.retry": "Try again",
    "overview.openHoursWeek": "opening hours over the next 7 days",
    "overview.openHoursDraft": "hours over the next 7 days (draft, not bookable yet)",
  },
  es: {
    "sched.templateServicesLabel": "Servicio(s) por defecto",
    "sched.windowServicesHint": "No marque nada para usar el/los servicio(s) por defecto del horario.",
    "sched.serviceOption": "{name} — {duration} min — {price}",
    "sched.noServices": "Todavía no hay ningún servicio activo. Cree primero un servicio: es lo que fija la duración de una cita.",
    "sched.buffer": "Pausa después de cada cita (min)",
    "sched.bufferHelp": "Tiempo de descanso después de cada cita, antes de la siguiente. Puede pasar de la hora de cierre. 0 = citas seguidas.",
    "sched.bufferRange": "Introduzca un número entero de minutos entre 0 y {max}.",
    "sched.windowNeedsService": "Esta franja no ofrece ningún servicio. Marque uno aquí, o elija servicios por defecto arriba.",
    "sched.windowNeedsCatalogue": "Esta franja no ofrece ningún servicio. Cree primero un servicio en Servicios: es lo que fija la duración de una cita.",
    "err.invalidWindowService": "Uno de los servicios elegidos en el horario semanal ya no está disponible en su catálogo. Vuelva a cargar la página y elija de nuevo.",
    "err.windowWithoutService": "Una franja no ofrece ningún servicio: elija uno para cada franja, o servicios por defecto.",
    "err.extraHoursOverlap": "Este horario se superpone con sus horas adicionales del {date}. Acorte la franja o elimine primero esas horas adicionales.",
    "sched.extraHoursOverlap": "Esta franja se superpone con sus horas adicionales del {date}.",
    "sched.timezoneHelp": "La zona horaria de su consulta: las horas de abajo están en ella, y sus clientes las ven convertidas a la suya.",
    "err.timeUnavailable": "Ese horario no está disponible: acaba de ser reservado, o se superpone con otra cita.",
    "err.invalidDate": "Fecha no válida",
    "ovr.extraTitle": "Horas adicionales",
    "ovr.extraSubtitle": "Horas de apertura puntuales, fuera de su horario semanal. Los horarios ofrecidos en ellas se calculan igual que en su horario habitual.",
    "ovr.extraAdd": "Añadir horas",
    "ovr.extraEmpty": "No hay horas adicionales próximas.",
    "ovr.extraServicesUnknown": "Servicio(s) retirado(s) del catálogo",
    "ovr.extraConfirmDelete": "¿Retirar estas horas adicionales? Las citas ya reservadas se conservan.",
    "ovr.extraDeleted": "Horas adicionales retiradas",
    "ovr.extraDialogTitle": "Añadir horas adicionales",
    "ovr.extraDialogDesc": "Abra su agenda en una fecha concreta, para los servicios que elija.",
    "ovr.extraDate": "Fecha",
    "ovr.extraStart": "Desde",
    "ovr.extraEnd": "Hasta",
    "ovr.extraServices": "Servicio(s) ofrecido(s)",
    "ovr.extraReasonPlaceholder": "p. ej.: sábado especial",
    "ovr.extraCreate": "Añadir estas horas",
    "ovr.extraCreated": "Horas adicionales añadidas",
    "ovr.extraOverlap": "Estas horas se superponen con su horario semanal o con otras horas adicionales de ese día.",
    "ovr.extraDateRequired": "Elija una fecha",
    "ovr.extraTimesRequired": "Hora obligatoria",
    "ovr.extraServicesRequired": "Marque al menos un servicio",
    "ovr.extraReasonTooLong": "{max} caracteres como máximo",
    "cal.openHoursDraft": "Su horario es un borrador: las horas sombreadas todavía no se ofrecen a sus clientes. Publíquelo desde Disponibilidad.",
    "cal.openHoursLoadError": "No se pudieron cargar sus horas de apertura",
    "cal.bufferAfter": "+ {n} min de pausa",
    "reschedule.err.sameTime": "Esta cita ya está en ese horario.",
    "reschedule.err.needsAttention": "Esta cita pagada espera su decisión (conservar o reembolsar): resuélvala desde Citas antes de moverla.",
    "reschedule.err.timeUnavailable": "Ese horario ya no está libre. Elija otro.",
    "reschedule.loadError": "No se pudieron cargar los horarios disponibles.",
    "reschedule.retry": "Reintentar",
    "overview.openHoursWeek": "horas de apertura en los próximos 7 días",
    "overview.openHoursDraft": "horas en los próximos 7 días (borrador, aún no reservables)",
  },
};
